import { useState } from "react";
import { useBreakpoint } from "@core/hooks/useBreakpoint";
import { useQueries, useQuery } from "@tanstack/react-query";
import { NewsSection } from "./NewsSection";
import { newsPageQuery } from "../../news.queries";

export type Props = {
  /** What follows the grid in the same band: the Get Updates card */
  children?: React.ReactNode;
  className?: string;
};

/**
 * Posts to a page: the design's three across and two down from a laptop up,
 * two by two on a tablet, and one at a time on a phone, as the grid lays
 * them out (NewsSection).
 */
export const NEWS_PAGE_SIZE = 6;
export const NEWS_PAGE_SIZE_TABLET = 4;
export const NEWS_PAGE_SIZE_PHONE = 1;

export function NewsSectionContainer({ children, className }: Props) {
  const isTablet = useBreakpoint("md");
  const isLaptop = useBreakpoint("lg");
  const pageSize = isLaptop ? NEWS_PAGE_SIZE : isTablet ? NEWS_PAGE_SIZE_TABLET : NEWS_PAGE_SIZE_PHONE;

  // When the screen changes size the pages are cut again, and the reader is
  // kept on the page holding the first post they were looking at.
  const [view, setView] = useState({ page: 1, pageSize });
  let page = view.page;
  if (view.pageSize !== pageSize) {
    page = Math.floor(((view.page - 1) * view.pageSize) / pageSize) + 1;
    setView({ page, pageSize });
  }
  const setPage = (next: number) => setView({ page: next, pageSize });

  // The first page says how many there are.
  const first = useQuery(newsPageQuery(1, pageSize));
  const totalPages = first.data?.total_pages ?? 1;

  // A page is fetched when it, or the page beside it, is in view, so the
  // next one is there before a swipe reaches it. One fetched stays.
  const rest = useQueries({
    queries: Array.from({ length: totalPages }, (_, i) => ({
      ...newsPageQuery(i + 1, pageSize),
      enabled: !!first.data && Math.abs(i + 1 - page) <= 1,
    })),
  });

  return (
    <NewsSection
      // A new page size is a new set of pages, laid out afresh on the page kept.
      key={pageSize}
      pages={rest.map((query, i) => (i === 0 ? first.data?.posts : query.data?.posts))}
      page={page}
      onPageChange={setPage}
      isLoading={first.isLoading}
      hrefFor={(post) => `/news/${post.slug}`}
      className={className}
    >
      {children}
    </NewsSection>
  );
}

export default NewsSectionContainer;
